// src/pages/purchase-orders/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../utils/supabase";

const BADGE_MAP = {
  slate: "bg-slate-800/20 text-slate-300 border border-slate-700/40",
  emerald: "bg-emerald-800/20 text-emerald-300 border border-emerald-700/40",
  sky: "bg-sky-800/20 text-sky-300 border border-sky-700/40",
  amber: "bg-amber-800/20 text-amber-300 border border-amber-700/40",
  blue: "bg-blue-800/20 text-blue-300 border border-blue-700/40",
};
const Badge = ({ children, color = "slate" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      BADGE_MAP[color] || BADGE_MAP.slate
    }`}
  >
    {children}
  </span>
);

const STATUS_COLOR = {
  unassigned: "amber",
  assigned: "sky",
  in_progress: "blue",
  done: "emerald",
  delivered: "slate",
};
const STATUS_LABEL = {
  unassigned: "Sin asignar",
  assigned: "Asignada",
  in_progress: "En proceso",
  done: "Terminada",
  delivered: "Entregada",
};

export default function PurchaseOrdersPage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [poStatus, setPoStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [poTechnicianName, setPoTechnicianName] = useState({});
  const [debouncedQ, setDebouncedQ] = useState("");

  // Obtiene nombre de técnico por OC a partir de service_orders
  const fetchTechnicianNameByPO = async (poIds) => {
    if (!poIds?.length) {
      setPoTechnicianName({});
      return;
    }
    const { data: osRows, error: osErr } = await supabase
      .from("service_orders")
      .select("po_id, technician_id")
      .in("po_id", poIds);
    if (osErr) throw osErr;

    const byPo = new Map();
    const techSet = new Set();
    for (const r of osRows || []) {
      if (!byPo.has(r.po_id)) byPo.set(r.po_id, new Set());
      if (r.technician_id) {
        byPo.get(r.po_id).add(r.technician_id);
        techSet.add(r.technician_id);
      }
    }

    const { data: techRows, error: techErr } = await supabase
      .from("technicians")
      .select("id, full_name")
      .in("id", Array.from(techSet));
    if (techErr) throw techErr;

    const nameById = (techRows || []).reduce((acc, t) => {
      acc[t.id] = t.full_name || "(sin nombre)";
      return acc;
    }, {});

    const result = {};
    for (const poId of poIds) {
      const set = byPo.get(poId) || new Set();
      if (set.size === 0) result[poId] = null;
      else if (set.size === 1) {
        const only = Array.from(set)[0];
        result[poId] = nameById[only] || "(técnico)";
      } else result[poId] = "Varios";
    }
    setPoTechnicianName(result);
  };

  const loadAll = async ({ search = "", status = "" } = {}) => {
    setLoading(true);
    setErr("");
    try {
      let query = supabase
        .from("v_purchase_orders")
        .select(
          "id,code,base_number,status,assigned_to,ops_priority,ops_status,ops_due_date,ops_notes,created_at,offer_id,offer_code,client_id,client_name,instrument_count"
        )
        .order("created_at", { ascending: false });
      if (status) query = query.eq("status", status);

      const term = String(search || "").trim();
      if (term) {
        const like = `%${term}%`;
        const orParts = [
          `code.ilike.${like}`,
          `offer_code.ilike.${like}`,
          `client_name.ilike.${like}`,
        ];

        const digits = term.replace(/\D/g, "");
        if (digits) {
          orParts.push(`base_number.eq.${Number(digits)}`);
        }

        query = query.or(orParts.join(","));
      }

      const { data, error } = await query;
      if (error) throw error;

      const result = data || [];
      setRows(result);
      await fetchTechnicianNameByPO(result.map((p) => p.id));
    } catch (e) {
      setErr(e.message || "No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await loadAll({ search: debouncedQ, status: poStatus });
      } catch (e) {
        if (!cancel) setErr(e.message || "No se pudo cargar la información");
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, poStatus]);

  // Realtime: actualizar cuando cambien OS u OCs
  useEffect(() => {
    const ch = supabase
      .channel("pos-os-realtime-purchase-orders")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "service_orders" },
        () => loadAll()
      )
      .on(
        "postgres_changes",
        { event: "update", schema: "public", table: "purchase_orders" },
        () => loadAll()
      )
      .subscribe();
    return () => supabase.removeChannel(ch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => rows || [], [rows]);

  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      <header className="flex items-center justify-between gap-3 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Órdenes de Compra</h1>
          <p className="text-sm text-muted-foreground">
            Gestión y seguimiento de OCs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por OC / oferta / cliente…"
            className="w-[260px] rounded-md border bg-background px-3 py-2 text-sm"
          />
          <select
            className="rounded-md border bg-background px-3 py-2 text-sm"
            value={poStatus}
            onChange={(e) => setPoStatus(e.target.value)}
          >
            <option value="">Estado: todos</option>
            <option value="unassigned">Sin asignar</option>
            <option value="assigned">Asignada</option>
            <option value="in_progress">En proceso</option>
            <option value="done">Terminada</option>
            <option value="delivered">Entregada</option>
          </select>
        </div>
      </header>

      {err && (
        <div className="mb-4 rounded border border-red-900/40 bg-red-900/10 p-3 text-sm text-red-300">
          {err}
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">Listado</h2>
          <Badge color="sky">{items.length}</Badge>
        </div>

        <div className="overflow-x-auto rounded border border-slate-800/60">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/40 text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">N° OC</th>
                <th className="px-3 py-2 text-left">Oferta</th>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Técnico</th>
                <th className="px-3 py-2 text-left"># Instr.</th>
                <th className="px-3 py-2 text-left">Creada</th>
                <th className="px-3 py-2 text-left">Actualizada</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-slate-400">
                    No hay órdenes para mostrar.
                  </td>
                </tr>
              )}
              {items.map((po) => (
                <tr
                  key={po.id}
                  className="border-t border-slate-800/60 hover:bg-slate-900/30"
                >
                  <td className="px-3 py-2 font-medium">
                    {po.code || `OC-${po.base_number}`}
                  </td>
                  <td className="px-3 py-2">{po.offer_code || "—"}</td>
                  <td className="px-3 py-2">{po.client_name || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge color={STATUS_COLOR[po.status] || "slate"}>
                      {STATUS_LABEL[po.status] || po.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {po.status === "unassigned"
                      ? "—"
                      : poTechnicianName[po.id] ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    {po.instrument_count ?? 0}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {po.created_at
                      ? new Date(po.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {po.updated_at
                      ? new Date(po.updated_at).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-400 mt-2">
          * El ingreso de instrumentos se realiza desde{" "}
          <strong>Comercial &gt; Ingresos</strong>.
        </p>
      </section>
    </div>
  );
}
